import React from "react";
import APSLogo from "../../../components/ui/APS Logo.svg";
import { formatPolicyDate } from "../../../utility/formatPolicyDate";

// ── Brand colours — explicit hex so html2canvas can read them (no CSS vars) ──
const C = {
  primary:    "#4361EE",
  success:    "#10B981",
  warning:    "#F59E0B",
  danger:     "#EF4444",
  text:       "#0F172A",
  muted:      "#64748B",
  light:      "#94A3B8",
  border:     "#E2E8F0",
  surface:    "#F8FAFC",
  indigo50:   "#EEF2FF",
  indigo100:  "#E0E7FF",
};

const verdictColor = (v = "") => {
  const lv = v.toLowerCase();
  if (["good", "strong", "excellent"].includes(lv)) return C.success;
  if (["neutral", "fair", "moderate"].includes(lv)) return C.warning;
  return C.danger;
};

const ProgressBar = ({ pct, color }) => (
  <div style={{ height: 8, background: C.border, borderRadius: 99, overflow: "hidden", flex: 1 }}>
    <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99 }} />
  </div>
);

/**
 * An off-screen, fixed-width snapshot of the full report.
 * Rendered as a hidden div and captured by html2canvas.
 */
const ReportPrintView = React.forwardRef(({ result, softwareName, privacyUrl }, ref) => {
  if (!result) return null;

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        left: -9999,
        top: 0,
        width: 794,          // ~A4 @ 96 dpi
        background: "#ffffff",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: 40,
        boxSizing: "border-box",
        color: C.text,
      }}
    >
      {/* ── Header ── */}
      <div data-pdf-block="true" style={{ marginBottom: 28 }}>
        <div>
          {/* Branding — marginRight replaces gap (html2canvas does not support gap) */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <img src={APSLogo} alt="APS Logo" style={{ height: 36, width: "auto", objectFit: "contain" }} />
          </div>
          {softwareName && (
            <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: 0 }}>
              {softwareName}
            </h1>
          )}
          {privacyUrl && (
            <p style={{ fontSize: 12, color: C.primary, marginTop: 6, wordBreak: "break-all" }}>{privacyUrl}</p>
          )}
          <p style={{ fontSize: 12, color: C.light, marginTop: 2 }}>Reviewed on {result.reviewedAt}</p>
          {formatPolicyDate(result.policyAsOf) && (
            <p style={{ fontSize: 12, color: C.light, marginTop: 2 }}>Policy As Of {formatPolicyDate(result.policyAsOf)}</p>
          )}
        </div>
      </div>

      {/* ── Score summary bar ── */}
      <div data-pdf-block="true" style={{
        display: "flex", alignItems: "center", padding: 24,
        background: C.indigo50, border: `1px solid ${C.indigo100}`, borderRadius: 16, marginBottom: 28,
      }}>
        {/* Circular score */}
        <div style={{
          width: 88, height: 88, borderRadius: "50%",
          border: `6px solid ${C.indigo100}`, borderTop: `6px solid ${C.primary}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: 28,
        }}>
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: 0, lineHeight: 1 }}>{result.overallScore}</p>
            <p style={{ fontSize: 10, color: C.light, margin: 0 }}>/ 10</p>
          </div>
        </div>

        <div style={{ flex: 1, marginRight: 28 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>Overall Privacy Score</p>
          <p style={{ fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
            Composite rating across data sharing, usage, retention, security and confidentiality.
          </p>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ fontSize: 10, color: C.light, textTransform: "uppercase", margin: 0 }}>Categories</p>
          <p style={{ fontSize: 32, fontWeight: 800, color: C.primary, margin: 0 }}>{result.categoriesAnalyzed}</p>
          {result.avgConfidence != null && (
            <>
              <p style={{ fontSize: 10, color: C.light, textTransform: "uppercase", margin: "8px 0 0" }}>Avg Confidence</p>
              <p style={{ fontSize: 32, fontWeight: 800, color: C.primary, margin: 0 }}>{result.avgConfidence}%</p>
            </>
          )}
        </div>
      </div>

      {/* ── Summaries ── */}
      <div data-pdf-block="true" style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 12px" }}>Summaries</h3>

        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>Tool Summary</p>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, margin: 0 }}>{result.toolSummary}</p>
        </div>

        <div>
          <p style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>Executive Summary</p>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, margin: 0 }}>{result.executiveSummary}</p>
        </div>
      </div>

      {/* ── Category breakdown ── */}
      <div data-pdf-block="true">
        <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 14px" }}>Category Breakdown</h3>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {result.categories.map((cat, idx) => {
            const scorePct = Math.round((cat.score / cat.maxScore) * 100);
            const vColor   = verdictColor(cat.verdict);
            const isLast = idx === result.categories.length - 1;
            return (
              <div key={cat.name} data-pdf-block="true" style={{
                border: `1px solid ${C.border}`, borderRadius: 14,
                padding: "16px 18px", background: "#fff",
                marginBottom: isLast ? 0 : 14,
              }}>
                {/* Title + verdict */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.3, marginBottom: 2 }}>{cat.name}</div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: vColor }}>{cat.verdict}</span>
                </div>

                {/* Score bar — marginRight/marginLeft replace gap */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: C.muted, width: 90, flexShrink: 0, marginRight: 10 }}>Score</span>
                  <ProgressBar pct={scorePct} color={C.success} />
                  <span style={{ fontSize: 11, color: C.muted, width: 36, textAlign: "right", marginLeft: 10, flexShrink: 0 }}>{cat.score}/{cat.maxScore}</span>
                </div>

                {/* Confidence bar */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: C.muted, width: 90, flexShrink: 0, marginRight: 10 }}>Confidence</span>
                  <ProgressBar pct={cat.confidence} color={C.primary} />
                  <span style={{ fontSize: 11, color: C.muted, width: 36, textAlign: "right", marginLeft: 10, flexShrink: 0 }}>{cat.confidence}%</span>
                </div>

                {/* Description */}
                {cat.description && (
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: 0, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                    {cat.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Footer ── */}
      <div data-pdf-block="true" style={{ marginTop: 36, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
        <p style={{ fontSize: 11, color: C.light, margin: 0 }}>Generated by PrivacyAI · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
        <p style={{ fontSize: 11, color: C.light, margin: 0 }}>Confidential — for internal use only</p>
      </div>
    </div>
  );
});

ReportPrintView.displayName = "ReportPrintView";
export default ReportPrintView;
